import { useEffect, useState } from "react";
import { Link, useNavigate } from "react-router-dom";
import { useAuth } from "../context/AuthContext";
import { Header } from "../components/Header";
import { Footer } from "../components/Footer";
import api, { API } from "../lib/api";
import { Button } from "../components/ui/button";
import { Input } from "../components/ui/input";
import { Code2, Copy, Trash2, KeyRound, Check } from "lucide-react";

export default function DevelopersPage() {
  const { user, loading } = useAuth();
  const navigate = useNavigate();
  const [keys, setKeys] = useState([]);
  const [keyName, setKeyName] = useState("");
  const [creating, setCreating] = useState(false);
  const [copied, setCopied] = useState(null);

  useEffect(() => {
    if (loading) return;
    if (!user || user === false) return;
    api.get("/dev/keys").then(({ data }) => setKeys(data));
  }, [user, loading]);

  const create = async (e) => {
    e.preventDefault();
    if (!keyName.trim()) return;
    setCreating(true);
    try {
      const { data } = await api.post("/dev/keys", { name: keyName.trim() });
      setKeys((k) => [data, ...k]);
      setKeyName("");
    } finally { setCreating(false); }
  };

  const remove = async (id) => {
    if (!window.confirm("Supprimer cette clé ?")) return;
    await api.delete(`/dev/keys/${id}`);
    setKeys((k) => k.filter((x) => x.id !== id));
  };

  const copy = (key) => {
    navigator.clipboard.writeText(key);
    setCopied(key);
    setTimeout(() => setCopied(null), 1500);
  };

  return (
    <div className="min-h-screen flex flex-col">
      <Header />
      <main className="flex-1 max-w-6xl w-full mx-auto px-4 sm:px-6 py-12">
        {/* Hero */}
        <div className="grid grid-cols-1 lg:grid-cols-5 gap-10 items-start mb-16">
          <div className="lg:col-span-3">
            <div className="inline-flex items-center gap-2 px-3 py-1 rounded-full border border-[var(--omni-border)] text-xs font-mono text-gray-600 mb-4">
              <Code2 size={13} /> API publique v1
            </div>
            <h1 className="text-4xl sm:text-5xl lg:text-6xl font-black tracking-tight mb-4">
              Construisez sur<br />
              <span style={{ color: "var(--omni-accent)" }}>OmniSearch</span>.
            </h1>
            <p className="text-lg text-gray-600 max-w-xl leading-relaxed">
              Une API REST simple pour intégrer la recherche du web, des images, des vidéos
              et des news dans n'importe quelle application. Sans tracker, sans surprise.
            </p>
          </div>
          <div className="lg:col-span-2">
            <pre className="bg-[#0a0a0a] text-[#00ff41] font-mono text-xs p-5 rounded-lg overflow-x-auto leading-relaxed">
{`# Bash
curl -H "X-API-Key: $OMNI_KEY" \\
  "${API}/v1/search?q=hello&type=web"`}
            </pre>
          </div>
        </div>

        {/* API Keys */}
        <section className="mb-16">
          <h2 className="text-2xl font-bold mb-6 flex items-center gap-2"><KeyRound size={20} /> Vos clés API</h2>
          {(!user || user === false) ? (
            <div className="border border-dashed border-[var(--omni-border)] rounded-lg p-10 text-center">
              <p className="text-gray-600 mb-4">Connectez-vous pour générer une clé API.</p>
              <Button asChild className="bg-[var(--omni-accent)] hover:bg-[var(--omni-accent-hover)] text-white rounded-full">
                <Link to="/login" state={{ from: "/developers" }} data-testid="dev-login-prompt">Se connecter</Link>
              </Button>
            </div>
          ) : (
            <>
              <form onSubmit={create} className="flex gap-2 mb-6" data-testid="create-key-form">
                <Input value={keyName} onChange={(e) => setKeyName(e.target.value)} placeholder="Nom de la clé (ex: Mon app mobile)" className="max-w-md" data-testid="key-name-input" />
                <Button type="submit" disabled={creating} className="bg-black hover:bg-gray-800 text-white rounded-full" data-testid="create-key-btn">
                  {creating ? "Création…" : "Générer"}
                </Button>
              </form>
              {keys.length === 0 ? (
                <p className="text-sm text-gray-500">Aucune clé pour le moment.</p>
              ) : (
                <div className="border border-[var(--omni-border)] rounded-lg overflow-hidden">
                  {keys.map((k) => (
                    <div key={k.id} className="flex flex-wrap items-center justify-between gap-3 px-4 py-3 border-b border-[var(--omni-border)] last:border-b-0 hover:bg-[var(--omni-surface)]" data-testid={`api-key-${k.id}`}>
                      <div className="flex-1 min-w-0">
                        <div className="font-medium">{k.name}</div>
                        <div className="font-mono text-xs text-gray-500 truncate">{k.key}</div>
                      </div>
                      <div className="text-xs text-gray-500">{k.usage_count} requêtes</div>
                      <div className="flex items-center gap-1">
                        <button onClick={() => copy(k.key)} className="p-2 rounded-md hover:bg-gray-100" data-testid={`copy-key-${k.id}`}>
                          {copied === k.key ? <Check size={14} className="text-green-600" /> : <Copy size={14} />}
                        </button>
                        <button onClick={() => remove(k.id)} className="p-2 rounded-md hover:bg-gray-100 text-red-600" data-testid={`delete-key-${k.id}`}>
                          <Trash2 size={14} />
                        </button>
                      </div>
                    </div>
                  ))}
                </div>
              )}
            </>
          )}
        </section>

        {/* Documentation */}
        <section>
          <h2 className="text-2xl font-bold mb-6">Documentation</h2>
          <div className="grid grid-cols-1 md:grid-cols-2 gap-6">
            <div className="border border-[var(--omni-border)] rounded-lg p-6">
              <h3 className="font-bold mb-2">GET /api/v1/search</h3>
              <p className="text-sm text-gray-600 mb-3">Recherche web, images, vidéos ou news.</p>
              <table className="w-full text-xs">
                <tbody>
                  <tr className="border-t border-[var(--omni-border)]"><td className="font-mono py-2">q</td><td className="text-gray-600">Requête (obligatoire)</td></tr>
                  <tr className="border-t border-[var(--omni-border)]"><td className="font-mono py-2">type</td><td className="text-gray-600">web | images | videos | news</td></tr>
                  <tr className="border-t border-[var(--omni-border)]"><td className="font-mono py-2">X-API-Key</td><td className="text-gray-600">Header obligatoire</td></tr>
                </tbody>
              </table>
            </div>
            <div className="border border-[var(--omni-border)] rounded-lg p-6">
              <h3 className="font-bold mb-2">Limites & quotas</h3>
              <ul className="text-sm text-gray-600 space-y-1.5">
                <li>· Plan gratuit : 1000 requêtes / mois</li>
                <li>· Pas de tracking, jamais.</li>
                <li>· Réponses au format JSON</li>
                <li>· Disponibilité : best-effort 99%</li>
              </ul>
            </div>
          </div>
        </section>
      </main>
      <Footer />
    </div>
  );
}
